import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, LoadingSpinner } from '@tadagram/ui';
import { apiService } from '../../services/api';
import { ExternalLink, Trash2, RefreshCw, Users, ChevronLeft, ChevronRight, Download, Plus, X } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GroupPostLead {
  id: string;
  business_id: string;
  requirement: string;
  criteria: string;
  group_url: string;
  post_url: string;
  author: string;
  author_url?: string;
  profile_url?: string;
  text: string;
  post_date?: string;
  collected_at: string;
}

interface LeadsResponse {
  leads: GroupPostLead[];
  total: number;
  page: number;
  limit: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatGroupLabel(url: string): string {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('groups');
    if (idx !== -1 && parts[idx + 1]) return `groups/${parts[idx + 1]}`;
    return u.hostname + u.pathname;
  } catch {
    return url;
  }
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function truncate(text: string, max = 200): string {
  if (!text) return '—';
  return text.length > max ? text.slice(0, max) + '…' : text;
}

function shortRequirement(req: string, max = 24): string {
  if (!req) return 'default';
  return req.length > max ? req.slice(0, max) + '…' : req;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const GroupPostLeadsDashboard: React.FC = () => {
  const { t } = useTranslation('leads');
  const [leads, setLeads] = useState<GroupPostLead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [requirements, setRequirements] = useState<string[]>([]);
  const [filterRequirement, setFilterRequirement] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // ── Manual create states ─────────────────────────────────────────────────
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addForm, setAddForm] = useState({ post_url: '', group_url: '', requirement: '', author: '', profile_url: '', text: '', post_date: '' });

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // ── Fetch leads ──────────────────────────────────────────────────────────
  const fetchLeads = useCallback(async (targetPage = 1, req = filterRequirement) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        page: String(targetPage),
        limit: String(limit),
      };
      if (req) params.requirement = req;

      const qs = new URLSearchParams(params).toString();
      const res = await apiService.get<{ success: boolean; data: LeadsResponse }>(
        `/api/v1/group-post-leads?${qs}`,
      );
      const data: LeadsResponse = res?.data ?? { leads: [], total: 0, page: 1, limit };
      setLeads(data.leads ?? []);
      setTotal(data.total ?? 0);
      setPage(data.page ?? 1);

      if (!req) {
        const seen = new Set<string>();
        (data.leads ?? []).forEach((l) => { if (l.requirement) seen.add(l.requirement); });
        setRequirements((prev) => Array.from(new Set([...prev, ...seen])).sort());
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filterRequirement, limit]);

  useEffect(() => {
    fetchLeads(1, filterRequirement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRequirementChange = (req: string) => {
    setFilterRequirement(req);
    fetchLeads(1, req);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchLeads(newPage, filterRequirement);
  };

  // ── Manual create ─────────────────────────────────────────────────────
  const handleManualCreate = async () => {
    if (!addForm.post_url.trim() || !addForm.group_url.trim()) {
      setAddError('Vui lòng nhập URL bài đăng và URL nhóm');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await apiService.post('/api/v1/group-post-leads', {
        post_url: addForm.post_url,
        group_url: addForm.group_url,
        requirement: addForm.requirement || undefined,
        author: addForm.author || undefined,
        profile_url: addForm.profile_url || undefined,
        text: addForm.text || undefined,
        post_date: addForm.post_date || undefined,
      });
      setShowAddModal(false);
      setAddForm({ post_url: '', group_url: '', requirement: '', author: '', profile_url: '', text: '', post_date: '' });
      fetchLeads();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      if (e?.response?.status === 409) {
        setAddError('Bài đăng này đã tồn tại trong hệ thống');
      } else {
        setAddError(e?.response?.data?.message || 'Lỗi khi thêm, thử lại sau');
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (lead: GroupPostLead) => {
    if (!window.confirm(t('deletePostConfirm', { author: lead.author, url: lead.post_url }))) return;
    setDeletingId(lead.id);
    try {
      await apiService.delete(`/api/v1/group-post-leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      alert(t('deleteFailed', { error: err?.message ?? '' }));
    } finally {
      setDeletingId(null);
    }
  };

  // ── Export to Excel (CSV with BOM) ───────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch ALL leads (up to 10000)
      const params: Record<string, string> = { page: '1', limit: '10000' };
      if (filterRequirement) params.requirement = filterRequirement;
      const qs = new URLSearchParams(params).toString();
      const res = await apiService.get<{ success: boolean; data: LeadsResponse }>(
        `/api/v1/group-post-leads?${qs}`,
      );
      const allLeads = res?.data?.leads ?? [];
      if (allLeads.length === 0) {
        alert(t('noDataToExport'));
        return;
      }

      // Build CSV
      const escapeCSV = (val: string) => {
        if (!val) return '';
        // Escape quotes and wrap in quotes if contains comma/newline/quote
        const escaped = val.replace(/"/g, '""');
        return /[,"\n\r]/.test(val) ? `"${escaped}"` : escaped;
      };

      const headers = ['Chủ đề', 'Nhóm', 'Tác giả', 'Profile URL', 'Nội dung', 'Link bài viết', 'Ngày đăng', 'Ngày thu thập'];
      const rows = allLeads.map((l) => [
        escapeCSV(l.requirement || ''),
        escapeCSV(l.group_url || ''),
        escapeCSV(l.author || ''),
        escapeCSV(l.profile_url || l.author_url || ''),
        escapeCSV(l.text || ''),
        escapeCSV(l.post_url || ''),
        escapeCSV(l.post_date || ''),
        escapeCSV(formatDate(l.collected_at)),
      ]);

      const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `leads_${filterRequirement || 'all'}_${dateStr}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(t('exportFailed', { error: err?.message ?? '' }));
    } finally {
      setExporting(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-500/10 text-blue-500 shrink-0">
            <Users size={18} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-text-primary">{t('groupTitle')}</h2>
            <p className="text-xs text-text-secondary mt-0.5">
              {t('groupSubtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleExport}
            disabled={exporting || loading || leads.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            title="Xuất dữ liệu ra file Excel (CSV)"
          >
            <Download size={14} className={exporting ? 'animate-bounce' : ''} />
            {exporting ? t('exporting') : t('exportExcel')}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shrink-0"
          >
            <Plus size={14} />
            Thêm thủ công
          </button>
          <button
            onClick={() => fetchLeads(1, filterRequirement)}
            disabled={loading}
            className="p-2 rounded-lg text-text-secondary hover:bg-bg-secondary hover:text-text-primary transition-colors disabled:opacity-40 shrink-0"
            title={t('refresh')}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-text-secondary whitespace-nowrap font-medium">{t('topicFilter')}</label>
        <select
          className="input text-sm py-1.5 w-auto min-w-[180px] max-w-xs"
          value={filterRequirement}
          onChange={(e) => handleRequirementChange(e.target.value)}
        >
          <option value="">{t('all')}</option>
          {requirements.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <span className="text-xs text-text-tertiary ml-auto">
          {t('totalPosts', { count: total })}
        </span>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-tg-danger/10 border border-tg-danger/30 text-tg-danger rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="md" text={t('loading')} />
        </div>
      ) : leads.length === 0 ? (
        <Card className="p-10 text-center">
          <Users size={40} className="mx-auto mb-3 text-text-tertiary opacity-40" />
          <p className="text-sm text-text-secondary">{t('noData')}</p>
          <p className="text-xs text-text-tertiary mt-1">{t('workerNote')}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-bg-secondary">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-28">{t('colTopic')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-36">{t('colGroup')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-32">{t('colAuthor')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide">{t('colContent')}</th>
                  <th className="px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-16 text-center">{t('colLink')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-24">{t('colPostDate')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide w-24">{t('colCollected')}</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-bg-secondary transition-colors duration-100">
                    {/* Requirement */}
                    <td className="px-4 py-3">
                      <span
                        className="badge badge-info inline-block max-w-[100px] truncate align-middle"
                        title={lead.requirement || 'default'}
                      >
                        {shortRequirement(lead.requirement)}
                      </span>
                    </td>

                    {/* Group */}
                    <td className="px-4 py-3">
                      <a
                        href={lead.group_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-tg-primary hover:underline max-w-[130px] truncate"
                        title={lead.group_url}
                      >
                        <span className="truncate">{formatGroupLabel(lead.group_url)}</span>
                        <ExternalLink size={10} className="shrink-0 opacity-60" />
                      </a>
                    </td>

                    {/* Author */}
                    <td className="px-4 py-3">
                      {lead.profile_url || lead.author_url ? (
                        <a
                          href={lead.profile_url || lead.author_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-text-primary hover:text-tg-primary max-w-[120px] truncate"
                          title={lead.author}
                        >
                          <span className="truncate">{lead.author || '—'}</span>
                          <ExternalLink size={10} className="shrink-0 opacity-50" />
                        </a>
                      ) : (
                        <span className="text-xs font-medium text-text-primary max-w-[120px] truncate block" title={lead.author}>
                          {lead.author || '—'}
                        </span>
                      )}
                    </td>

                    {/* Content */}
                    <td className="px-4 py-3">
                      <p className="text-xs text-text-primary leading-relaxed line-clamp-3" title={lead.text}>
                        {truncate(lead.text, 200)}
                      </p>
                    </td>

                    {/* Post link */}
                    <td className="px-4 py-3 text-center">
                      {lead.post_url ? (
                        <a
                          href={lead.post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-tg-primary/10 text-tg-primary hover:bg-tg-primary/20 transition-colors"
                          title={lead.post_url}
                        >
                          <ExternalLink size={13} />
                        </a>
                      ) : (
                        <span className="text-text-tertiary text-xs">—</span>
                      )}
                    </td>

                    {/* Post date */}
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-text-secondary">
                      {lead.post_date || '—'}
                    </td>

                    {/* Collected at */}
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-text-secondary">
                      {formatDate(lead.collected_at)}
                    </td>

                    {/* Delete */}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(lead)}
                        disabled={deletingId === lead.id}
                        className="p-1.5 rounded text-text-tertiary hover:text-tg-danger hover:bg-tg-danger/10 transition-colors disabled:opacity-40"
                        title="Xóa"
                      >
                        {deletingId === lead.id ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-1">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1 || loading}
            className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs text-text-secondary">
            Trang {page} / {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages || loading}
            className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Manual Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-bg-primary rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold text-text-primary">Thêm post lead thủ công</h3>
              <button onClick={() => { setShowAddModal(false); setAddError(null); }} className="p-1.5 rounded-lg text-text-secondary hover:bg-bg-secondary transition-colors"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              {addError && <div className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">{addError}</div>}
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">URL bài đăng <span className="text-red-500">*</span></label>
                <input type="url" value={addForm.post_url} onChange={e => setAddForm(f => ({ ...f, post_url: e.target.value }))} placeholder="https://facebook.com/..." className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">URL nhóm <span className="text-red-500">*</span></label>
                <input type="url" value={addForm.group_url} onChange={e => setAddForm(f => ({ ...f, group_url: e.target.value }))} placeholder="https://facebook.com/groups/..." className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Chủ đề (Requirement)</label>
                <input type="text" value={addForm.requirement} onChange={e => setAddForm(f => ({ ...f, requirement: e.target.value }))} placeholder="Chủ đề bài đăng..." className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Ngày đăng</label>
                <input type="date" value={addForm.post_date} onChange={e => setAddForm(f => ({ ...f, post_date: e.target.value }))} className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Tác giả</label>
                <input type="text" value={addForm.author} onChange={e => setAddForm(f => ({ ...f, author: e.target.value }))} placeholder="Tên tác giả..." className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">URL profile tác giả</label>
                <input type="url" value={addForm.profile_url} onChange={e => setAddForm(f => ({ ...f, profile_url: e.target.value }))} placeholder="https://facebook.com/..." className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Nội dung</label>
                <textarea value={addForm.text} onChange={e => setAddForm(f => ({ ...f, text: e.target.value }))} rows={3} placeholder="Nội dung bài đăng..." className="w-full px-3 py-2 text-sm bg-bg-secondary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 resize-none" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <button onClick={() => { setShowAddModal(false); setAddError(null); }} className="px-4 py-2 text-sm text-text-secondary hover:bg-bg-secondary rounded-lg transition-colors">Hủy</button>
              <button onClick={handleManualCreate} disabled={addLoading} className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50">{addLoading ? 'Đang lưu...' : 'Thêm lead'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
